import React, { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

/**
 * Una conversación de WhatsApp que se escribe sola, para mostrar cómo atiende
 * el bot sin que haya que instalar nada ni tener el teléfono a mano.
 *
 * No es un video ni un gif: son los mismos globos de la app, así que pesa nada
 * y se ve nítido en cualquier pantalla.
 *
 * La conversación arranca cuando el bloque entra en pantalla y se repite en
 * loop. Cada mensaje del bot va precedido por los tres puntitos de "escribiendo",
 * que es lo que hace que se lea como una conversación real y no como una lista.
 */

/**
 * El guion por defecto. `de` es quién habla: 'cliente' o 'bot'.
 *
 * Empieza por una consulta y no por la reserva a propósito: la mayoría de los
 * mensajes que le llegan a un negocio no son "quiero un turno" sino cuánto
 * sale, dónde estás, hasta qué hora abrís o si atendés tal cosa. Que el bot
 * conteste eso es lo que le saca el teléfono de encima al dueño; tomar el turno
 * viene después y acá se ve al final.
 *
 * `confirma: true` marca el mensaje en el que el turno queda tomado. No cambia
 * cómo se dibuja el globo: es la señal que dispara `alConfirmar`, para que quien
 * monte el simulador pueda mostrar la consecuencia al lado (ver
 * `DelChatALaAgenda`).
 */
const CONVERSACION = [
  { de: 'cliente', texto: 'Hola! Cuánto sale el corte con color?' },
  { de: 'bot', texto: '¡Hola! 👋 Corte + color está $45.000 y lleva unas 2 horas.' },
  { de: 'cliente', texto: 'Y dónde quedan? tienen estacionamiento?' },
  { de: 'bot', texto: 'Estamos en Av. Rivadavia 1245, a dos cuadras del subte.\nHay estacionamiento medido sobre la misma cuadra 🅿️' },
  { de: 'cliente', texto: 'Buenísimo. Tenés lugar el jueves?' },
  { de: 'bot', texto: 'El jueves me queda 15:00, 16:30 y 18:00. ¿Cuál te viene bien?' },
  { de: 'cliente', texto: 'El de las 16:30' },
  {
    de: 'bot',
    texto: 'Turno confirmado ✅\nJueves 16:30 · Corte + color\nTe mando un recordatorio el día anterior.',
    confirma: true,
  },
];

/** Cuánto tarda en "escribir" un mensaje, según su largo. */
const tiempoDeEscritura = (texto) => Math.min(1800, 420 + texto.length * 18);
const PAUSA_ENTRE_MENSAJES = 550;
const PAUSA_ANTES_DE_REPETIR = 3200;

/**
 * @param conversacion  El guion a escribir. TIENE QUE SER UNA REFERENCIA
 *   ESTABLE —una constante de módulo, no un literal escrito en el JSX—: entra
 *   en las dependencias del efecto de abajo, así que un array nuevo en cada
 *   render reinicia el bucle antes de que llegue a escribir nada y la
 *   conversación se queda para siempre en el primer mensaje.
 * @param repetir  Si es `false`, la conversación se escribe UNA vez y queda
 *   completa en pantalla; no vuelve a empezar. Existe por el hero de
 *   /landing-2: ahí el simulador va adentro de un teléfono grande, y el
 *   `setVisibles([])` con el que arranca cada vuelta dejaba la pantalla vacía
 *   cada quince segundos. En una tarjeta chica ese parpadeo no se nota y el
 *   bucle es lo correcto; en una maqueta de hero, que alguien mira dos
 *   segundos, la probabilidad de agarrarla en blanco es alta. Con `false` se
 *   conserva lo que importa —que la conversación se escriba sola, que es la
 *   prueba de que el bot contesta— sin el estado vacío.
 * @param alConfirmar  Se llama con `true` cuando aparece el mensaje marcado
 *   `confirma` y con `false` cada vez que el bucle vuelve a empezar. Es cómo
 *   `DelChatALaAgenda` sabe en qué momento prender el turno en la agenda de al
 *   lado. Opcional: el hero y el mosaico montan el simulador solo.
 */
export default function SimuladorWhatsApp({ conversacion = CONVERSACION, alConfirmar, repetir = true }) {
  const referencia = useRef(null);
  const enPantalla = useInView(referencia, { amount: 0.4 });
  const menosMovimiento = useReducedMotion();
  const [visibles, setVisibles] = useState([]);
  const [escribiendo, setEscribiendo] = useState(false);
  const contenedor = useRef(null);

  // El aviso va por referencia y NO por dependencia del efecto: quien monte
  // esto va a pasar casi siempre una función escrita en el JSX, que es distinta
  // en cada render. Como dependencia, cada render del padre cancelaría el bucle
  // y lo volvería a arrancar desde el primer mensaje.
  const avisar = useRef(alConfirmar);
  useEffect(() => {
    avisar.current = alConfirmar;
  });

  useEffect(() => {
    // Con menos movimiento pedido, se muestra la conversación entera quieta:
    // la información es la misma, lo que se saca es la animación. El turno ya
    // está tomado en esa foto, así que la consecuencia se avisa igual.
    if (menosMovimiento) {
      setVisibles(conversacion.map((_, i) => i));
      avisar.current?.(true);
      return;
    }
    if (!enPantalla) return;

    let cancelado = false;
    const esperas = [];
    const esperar = (ms) => new Promise((seguir) => esperas.push(setTimeout(seguir, ms)));

    const correr = async () => {
      let primeraVuelta = true;
      while (!cancelado && (repetir || primeraVuelta)) {
        primeraVuelta = false;
        /* EL PRIMER MENSAJE YA ESTÁ PUESTO, no se escribe.

           Antes el bucle arrancaba con la pantalla vacía, esperaba 600ms y
           recién ahí empezaba con el primero — que al ser del cliente sumaba
           otros 550 de pausa. O sea 1,15 segundos de pantalla vacía antes de
           que apareciera nada, y en un teléfono grande eso es un rectángulo
           beige enorme que parece roto.

           Además es lo que pasa de verdad: cuando abrís una conversación, el
           mensaje que te mandaron YA ESTÁ AHÍ. Lo que se mira escribirse es la
           respuesta, no la pregunta. */
        setVisibles([0]);
        setEscribiendo(false);
        avisar.current?.(conversacion[0]?.confirma === true);
        for (let i = 1; i < conversacion.length; i++) {
          if (cancelado) return;
          const mensaje = conversacion[i];
          if (mensaje.de === 'bot') {
            setEscribiendo(true);
            await esperar(tiempoDeEscritura(mensaje.texto));
            if (cancelado) return;
            setEscribiendo(false);
          } else {
            await esperar(PAUSA_ENTRE_MENSAJES);
          }
          if (cancelado) return;
          setVisibles((previos) => [...previos, i]);
          if (mensaje.confirma) avisar.current?.(true);
          await esperar(PAUSA_ENTRE_MENSAJES);
        }
        await esperar(PAUSA_ANTES_DE_REPETIR);
      }
    };

    correr();
    return () => {
      cancelado = true;
      esperas.forEach(clearTimeout);
    };
  }, [enPantalla, menosMovimiento, conversacion, repetir]);

  // Que el último mensaje quede siempre a la vista, como en el teléfono.
  useEffect(() => {
    const caja = contenedor.current;
    if (caja) caja.scrollTop = caja.scrollHeight;
  }, [visibles, escribiendo]);

  return (
    <div ref={referencia} className="flex min-h-0 flex-1 flex-col">
      <div
        ref={contenedor}
        // `aria-hidden`: es una demostración decorativa. El texto que importa
        // está en el título y la bajada de la tarjeta; leer los seis globos en
        // loop con un lector de pantalla sería ruido.
        aria-hidden="true"
        className="flex min-h-[260px] flex-1 flex-col justify-end gap-2 overflow-hidden rounded-2xl bg-neutral-50 p-3"
      >
        {conversacion.map((mensaje, i) =>
          visibles.includes(i) ? (
            <p
              key={i}
              className={
                mensaje.de === 'cliente'
                  ? 'ml-auto w-fit max-w-[85%] animate-[aparecerGlobo_.28s_ease-out] whitespace-pre-line rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-[13px] leading-snug text-primary-foreground'
                  : 'w-fit max-w-[90%] animate-[aparecerGlobo_.28s_ease-out] whitespace-pre-line rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-[13px] leading-snug text-neutral-800 shadow-sm'
              }
            >
              {mensaje.texto}
            </p>
          ) : null
        )}

        {escribiendo && (
          <span className="flex w-fit items-center gap-1 rounded-2xl rounded-bl-sm bg-white px-3 py-2.5 shadow-sm">
            {[0, 1, 2].map((p) => (
              <span
                key={p}
                className="h-1.5 w-1.5 animate-[latirPunto_1s_ease-in-out_infinite] rounded-full bg-neutral-400"
                style={{ animationDelay: `${p * 0.16}s` }}
              />
            ))}
          </span>
        )}
      </div>
    </div>
  );
}
